import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { HtmlMetaManagerService } from "modules/app/services/html-meta-manager.service";
import { LoaderService } from "modules/app/services/loader.service";
import { AppHelperService } from "modules/app/services/app-helper.service";
import { TranslateService } from "@ngx-translate/core";
import { ProductViewHelperService } from './../../services/product-view-helper.service';
import { ContentAccessHelperService } from './../../services/content-access-helper.service';

interface Item {
    items: TileItem[];
    folderName: string;
}

interface TileItemFolder {
    "item-type": "folder";
    name: string;
    "item-code": string;
    "sub-type": string;
    items: TileItem[];
    sequence: number;
    accessInfo: any;
}

interface TileItemResource {
    "item-type": "resource";
    name: string;
    "item-code": string;
    model: any;
    "pass-score"?: number;
    "contributes-to-completion"?: boolean;
    resource?: string;
    "sub-type"?: string;
    description?: string;
    "ext-cup-scompscoreable"?: {
        container?: string;
        "launch-url"?: string;
    };
    accessInfo: any;
    image_url?: string;
}

type TileItem = TileItemFolder | TileItemResource;

@Component({
    selector: "product-view-v4",
    templateUrl: "./product-view-v4.template.html",
    styleUrl: "./product-view-v4.style.scss",
    standalone: false
})
export class ProductViewV4 implements OnInit, OnDestroy {
    @Input() accessibleUnitCode: string = '';
    // modal references
    @ViewChild('activityLockModal') activityLockModal : ElementRef;
    @ViewChild('activityLockModalCancel') activityLockModalCancel : ElementRef;
    @ViewChild('showActivityLockModal') showActivityLockModal : ElementRef;

    private productData: any;
    private bundleDetails: any;
    private productCode: string;
    private spaceId: string;
    private classId: string;
    private bundleProduct: any;
    private itemStack: Item[] = [];
    private userProfileData: any;
    private readonly componentSubscriptions: Subscription = new Subscription();

    public productMeta: any;
    public currentItems: TileItem[] = [];
    public categoryHeading: string = "";
    public imagesBasePath: string = "";
    public selectedItem: TileItemResource;
    public selectedActivity: any;
    public unlockDetail: any;
    public classEndDate: number;

    isHideBackButton:boolean = false;
    isLtiUser:boolean = false;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly loaderService: LoaderService,
        private readonly htmlMetaManagerService: HtmlMetaManagerService,
        private readonly translate: TranslateService,
        private readonly router: Router,
        public readonly productViewHelperService: ProductViewHelperService,
        public appHelperService: AppHelperService,
        private readonly contentAccessHelperService: ContentAccessHelperService
    ) { }

    ngOnInit() {
        // Subscribe to route params to extract product code and data
        this.componentSubscriptions.add(this.route.params.subscribe((params: Params) => {
            this.spaceId = params.spaceid;
            this.classId = params.classid;
            this.productCode = params.productcode;
            this.productData = this.route.snapshot.data?.resolveData?.productData;
            this.productMeta = this.productData?.meta?.meta;
            this.bundleDetails = this.productData?.bundleDetails;
            this.userProfileData = this.route.snapshot.data?.resolveData?.userProfileData;
            this.imagesBasePath = this.productMeta?.paths?.['public-assets'];
            this.bundleProduct = this.bundleDetails?.products?.find((bP) => bP?.['dls-product']?.['code'] === this.productCode);
            this.isLtiUser = this.userProfileData.launchType === 'lti';
            this.classEndDate = this.productData?.classmeta?.class?.enddate;

            // Initialize current view level
            this.initialiseView();
            // Check if we need to initialize the item stack, in case, an activity is accessed directly
            if (this.route.snapshot.firstChild && !this.itemStack.length) {
                const itemCode = this.route.snapshot.firstChild.params['itemid'];
                const itemParts = itemCode.split('/');
                let partCode = itemParts[0];

                // Traverse through item parts to create the current item stack (folder structure)
                for (let i = 0; i < itemParts.length - 1; i++) {
                    const item = this.currentItems.find(item => item['item-code'] === partCode);
                    if (item?.['item-type'] === 'folder' && item?.items?.length) {
                        this.itemStack.push({
                            items: this.currentItems,
                            folderName: this.categoryHeading
                        });
                        this.currentItems = item.items;
                        this.categoryHeading = item.name;
                    }
                    if (i < itemParts.length - 1) {
                        partCode += `/${itemParts[i + 1]}`;
                    }
                }

                // Update page title and description
                this.setPageTitleAndDesc();
            }
        }));

        this.componentSubscriptions.add(this.productViewHelperService.selectedItemId.subscribe((selectedItemObject: any) => { 
            this.selectedActivity = selectedItemObject?.selectedItemData;
        }));
    }

    /**
     * Reset the component to its initial state
     * @param resetStack - Whether to reset the item stack
     */
    public initialiseView(resetStack: boolean = true): void {
        if (resetStack) {
            this.currentItems = this.productData?.toc?.tocArray?.result || [];
            this.categoryHeading = this.bundleProduct?.meta?.["lp-header"];
            this.itemStack = [];
        }

        this.productViewHelperService.selectedItemId.next(null);
        // Set page title and description
        this.setPageTitleAndDesc();
        this.loaderService.hideLoader();
        this.isHideBackButton = ['ltd'].indexOf(this.route.snapshot.queryParams.lf) === -1 && this.itemStack.length === 0;
        // If user is LTI user and only one item exists and unit deeplink is launched, navigate into it directly
        if(this.isLtiUser && this.currentItems.length === 1 && this.accessibleUnitCode) {
            this.nextCategory(this.currentItems[0]);
        }
    }

    /**
     * Navigate into next folder or activity
     * @param item - The TileItem to navigate into
     */
    public nextCategory(item: TileItem): void {
        if (item["item-type"] === "folder") {
            this.itemStack.push({
                items: this.currentItems,
                folderName: this.categoryHeading
            });
            this.currentItems = item.items;
            this.categoryHeading = item.name;
            this.setPageTitleAndDesc();
        } else if (item["item-type"] === "resource") {
            // Check if the resource is locked
            if (item.accessInfo?.isLocked) {
                const activeRule = this.contentAccessHelperService.getCurrentActiveRule(
                    item.accessInfo?.finalResultantLockDetails,
                    item['item-code'],
                    this.productViewHelperService.getIsAssignmentOnlyProduct()
                );
                this.unlockDetail = activeRule?.unlockRuleDetails;
                // Open the locked content modal
                this.showActivityLockModal?.nativeElement?.click();
                return;
            }
            this.loaderService.showOverlayLoader();
            const requiredQueryParams = this.appHelperService.getRequiredQueryParams(this.route.snapshot.queryParams, ['lf', 'cid', 'bcode', 'rcode', 'ltik', 'ltiGiId']);
            this.router.navigate(["item", item["item-code"]], { relativeTo: this.route, queryParams: requiredQueryParams });
        }
        this.isHideBackButton = ['ltd'].indexOf(this.route.snapshot.queryParams.lf) === -1 && this.itemStack.length === 0;
    }

    /**
     * Navigate back to previous folder level
     */
    public goBack(): void {
        if (this.itemStack.length > 0) {
            const last = this.itemStack.pop();
            this.isHideBackButton = ['ltd'].indexOf(this.route.snapshot.queryParams.lf) === -1 && this.itemStack.length === 0;
            this.currentItems = last.items;
            this.categoryHeading = last.folderName;
            this.setPageTitleAndDesc();
        } else {
            const { queryParams } = this.route.snapshot;
            // If we are at the root level, navigate to Dashboard App for student
            if (queryParams.lf == 'ltd') {
                this.appHelperService.navigateToOnboardingApp(['lti-teacher-dashboard', this.spaceId, 'class', queryParams.cid || this.classId, 'ltik', queryParams.ltik]);
            } else if (this.userProfileData?.role === "student") { // If we are at the root level, navigate to Dashboard App for student
                this.appHelperService.navigateToDashboardApp(["learner", "dashboard"]);
            } else if (this.classId) {
                    this.appHelperService.navigateToClassApp(["teacher", this.spaceId, "class", this.classId, "view/materials"]);
            } else {
                const bundleCode = this.bundleDetails["bundle-code"];
                this.appHelperService.navigateToDashboardApp(["teacher", this.spaceId, "bundle", bundleCode, "view"]);
            }
            this.categoryHeading = this.bundleProduct?.meta?.["lp-header"];
            this.setPageTitleAndDesc();
        }
    }

    /**
     * Set the page title and description
     */
    private setPageTitleAndDesc(): void {
        const customTitle = this.translate.instant("HTML_META.MAIN.CUSTOM_TITLE");

        let title: string;
        // Check if current level contains an activity
        if (this.currentItems?.some(item => item["item-type"] === "resource")) {
            title = this.translate.instant("HTML_META.SCOMP_ACTIVITIES.TITLE");
        } else {
            title = this.translate.instant("HTML_META.SCOMP_PRODUCT.TITLE");
        }

        this.htmlMetaManagerService.setTitle(`${title} ${customTitle}`, this.bundleDetails.title);
        this.htmlMetaManagerService.setDescription(
            this.translate.instant("HTML_META.SCOMP_PRODUCT.DESCRIPTION")
        );
    }

    ngOnDestroy(): void {
        this.componentSubscriptions?.unsubscribe();

        // Close activity lock modal if it is open
        if (this.activityLockModal.nativeElement.classList.contains('show')) {
            this.activityLockModalCancel.nativeElement.click();
        }
    }
}